import Decimal from 'decimal.js';
import { Rates, InsurancePlan, PremiumRate, AgeTiers, Product } from './InsuranceModels';

const getRateForAge = (age: number, rates: PremiumRate[]): Decimal | null => {
  for (const rate of rates) {
    const matchesMax = rate.max === null || age <= rate.max;
    if (age >= rate.min && matchesMax) {
      return new Decimal(rate.rate);
    }
  }
  return null;
};

const getAgeTier = (ageTiers: AgeTiers, primaryKey: string, fallbackKey?: string): PremiumRate[] => {
  const primaryRates = ageTiers[primaryKey];
  if (primaryRates?.length) {
    return primaryRates;
  }
  const fallbackRates = fallbackKey ? ageTiers[fallbackKey] : undefined;
  return fallbackRates ?? [];
};

const getCoverPremium = (coverAmount: number, rate: Decimal): Decimal => {
  return new Decimal(coverAmount).dividedBy(1000).times(rate);
};

const CalculatePremium = (insurancePlan: InsurancePlan, rates: Product, globals: Rates): InsurancePlan => {
  const mainMemberRate = getRateForAge(insurancePlan.Principal.age, getAgeTier(rates.ageTiers, 'main'));

  if (mainMemberRate === null) {
    return insurancePlan;
  }

  const principalPremium = getCoverPremium(insurancePlan.Principal.coverAmount, mainMemberRate)
    .plus(globals.repatIndividual);

  let updatedPlan: InsurancePlan = {
    ...insurancePlan,
    Principal: {
      ...insurancePlan.Principal,
      premiumAmount: principalPremium
    },
    Family: {
      ...insurancePlan.Family,
      premiumAmount: new Decimal(0),
      waivedPremiumAmount: 0
    }
  };

  let totalPremium = principalPremium;
  const isChildrenSelected = updatedPlan.Family.Children.selected;
  const isFamilySelected = updatedPlan.Family.Spouse.selected || isChildrenSelected;

  if (isFamilySelected) {
    const familyRateTable = isChildrenSelected
      ? getAgeTier(rates.ageTiers, 'family', 'husbandAndWife')
      : getAgeTier(rates.ageTiers, 'husbandAndWife', 'family');
    const familyRate = getRateForAge(updatedPlan.Principal.age, familyRateTable);

    if (familyRate !== null) {
      const familyPremium = getCoverPremium(updatedPlan.Principal.coverAmount, familyRate)
        .plus(globals.repatFamily);

      updatedPlan = {
        ...updatedPlan,
        Family: {
          ...updatedPlan.Family,
          premiumAmount: familyPremium
        }
      };
      totalPremium = familyPremium;
    }
  }

  if (updatedPlan.Parent.selected) {
    const parentRateTable = getAgeTier(rates.ageTiers, 'parents', 'main');
    const updatedParents = updatedPlan.Parent.parents.map((parent) => {
      const rateForParent = getRateForAge(parent.age, parentRateTable);
      const premiumForParent = rateForParent === null
        ? new Decimal(0)
        : getCoverPremium(parent.coverAmount, rateForParent);
      return { ...parent, premiumAmount: premiumForParent };
    });

    updatedPlan = {
      ...updatedPlan,
      Parent: { ...updatedPlan.Parent, parents: updatedParents }
    };

    totalPremium = updatedParents.reduce(
      (acc, curr) => acc.plus(curr.premiumAmount || new Decimal(0)),
      totalPremium
    );
  }

  if (updatedPlan.ExtendedFamily.selected) {
    const extendedRateTable = getAgeTier(rates.ageTiers, 'extendedFamily');
    const updatedExtended = updatedPlan.ExtendedFamily.extendedMembers.map((member) => {
      const rateForMember = getRateForAge(member.age, extendedRateTable);
      const premiumForMember = rateForMember === null
        ? new Decimal(0)
        : getCoverPremium(member.coverAmount, rateForMember);
      return { ...member, premiumAmount: premiumForMember };
    });

    updatedPlan = {
      ...updatedPlan,
      ExtendedFamily: { ...updatedPlan.ExtendedFamily, extendedMembers: updatedExtended }
    };

    totalPremium = updatedExtended.reduce(
      (acc, curr) => acc.plus(curr.premiumAmount || new Decimal(0)),
      totalPremium
    );
  }

  updatedPlan.totalPremiumAmount = totalPremium;
  return updatedPlan;
};

export { CalculatePremium };